import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { cn } from '../lib/cn';
/**
 * The organizer console's labelled field, and the control metrics that go with it.
 *
 * ## Transcribed, not adapted
 *
 * Every measurement below is read off `Organizer Setup Desktop.dc.html` step 3 ("How members will
 * know you"), which repeats this shape seven times:
 *
 * ```
 * field   display:flex; flex-direction:column; gap:6px
 * label   font-family:var(--font-brand); font-weight:600; font-size:var(--text-sm);
 *         color:var(--text-strong)
 * control display:flex; align-items:center; gap:10px; height:46px;
 *         border:1px solid var(--border-default); border-radius:var(--radius-md);
 *         background:#fff; padding:0 14px; font-size:var(--text-md)
 *         color:var(--text-subtle) when empty, var(--text-strong) when filled
 * ```
 *
 * **This is not the internal console's field.** That one's control is 36px (`Input`'s `h-9`) with
 * a 12px label; this is 46px with a 14px label and a 12px radius against `Input`'s 8px. The
 * difference is small enough to look like rounding and is not — three separate agents have now
 * found the two consoles' components differing by a pixel or two and a font weight. The two sets
 * of numbers are recorded next to their own transcription rather than averaged into one control:
 * `Input`/`Textarea` keep the admin console's, and these are the organizer console's.
 *
 * The mobile variant (`Organizer Setup Mobile.dc.html` step `at_m5`) is 50px rather than 46, with
 * a 16px field gap rather than 18. That is a responsive step on the same control, expressed as
 * `h-[50px] sm:h-[46px]` in `FIELD_CONTROL_CLASSES` rather than a second component.
 */
export const FIELD_CONTROL_CLASSES = 'flex h-[50px] w-full items-center gap-[10px] rounded-md border border-border bg-card px-[14px] ' +
    'text-md text-foreground placeholder:text-[hsl(var(--gray-400))] ' +
    'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 ' +
    'focus-visible:ring-ring/40 sm:h-[46px]';
/**
 * The refusal treatment. Applied on top of {@link FIELD_CONTROL_CLASSES} rather than replacing
 * them, so a field that is refused is the same control with a red edge — `ORG-013` AC-3 marks the
 * failing field *in place*, which means it must not move or resize when it fails.
 */
export const FIELD_CONTROL_INVALID_CLASSES = 'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30';
export function Field({ label, labelSuffix, htmlFor, hint, error, counter, children, className, }) {
    const describedBy = `${htmlFor}-description`;
    return (_jsxs("div", { className: cn('flex flex-col gap-1.5', className), children: [_jsxs("label", { htmlFor: htmlFor, className: "font-brand text-sm font-semibold text-foreground", children: [label, labelSuffix === undefined ? null : (_jsxs("span", { className: "font-normal text-[hsl(var(--gray-400))]", children: [" ", labelSuffix] }))] }), children, hint === undefined && counter === undefined && error === undefined ? null : (_jsxs("div", { className: "flex justify-between gap-4", children: [_jsx("span", { id: describedBy, className: "text-xs leading-[1.5] text-muted-foreground", children: error === undefined ? (hint) : (_jsx("span", { role: "alert", className: "font-semibold text-destructive", children: error })) }), counter === undefined ? null : (_jsx("span", { className: "shrink-0 text-xs leading-[1.5] text-muted-foreground", children: counter }))] }))] }));
}
/**
 * The live character counter, rendered `{used} / {max}` (`ORG-014` AC-6 states that format
 * exactly, and the prototype shows `218 / 400`).
 *
 * It turns muted-red past the limit rather than refusing the keystroke: a counter that stops
 * typing loses the end of a sentence somebody was mid-way through, and the save refuses anyway.
 */
export function CharacterCount({ used, max, }) {
    return (_jsxs("span", { className: cn(used > max && 'font-semibold text-destructive'), children: [used, " / ", max] }));
}
//# sourceMappingURL=field.js.map